import clsx from 'clsx';
import _floor from 'lodash/floor';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { FormRadioGroup } from '@/components/ui/radio-group';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';

import { useChannelFormContext } from './channel-form-context';

interface BudgetAllocationProps {
  channelKey: string;
  isReadOnly: boolean;
}

export function BudgetAllocation({
  channelKey,
  isReadOnly = false,
}: BudgetAllocationProps) {
  const { handleBudgetAllocationChange } = useChannelFormContext();
  const { hookForm } = useJobPostingContext();

  const channel = useWatch({
    control: hookForm.control,
    name: `channels.${channelKey}`,
  });

  const { openingBudgets, budgetAllocation, channelBudget } = channel;

  const hasMultipleOpenings = Object.keys(openingBudgets).length > 1;

  const perOpeningBudget = useMemo(() => {
    if (!channelBudget) return null;
    return _floor(channelBudget / Object.keys(openingBudgets).length, 2);
  }, [channelBudget, openingBudgets]);

  const budgetOptions = [
    {
      value: 'evenly',
      label: (
        <FormattedMessage
          defaultMessage="Allocate evenly across openings"
          description="Label for the evenly allocated budget option"
        />
      ),
      id: 'evenly-allocated',
    },
    {
      value: 'per-funnel',
      label: (
        <FormattedMessage
          defaultMessage="Allocate spend per opening"
          description="Label for the per-funnel allocated budget option"
        />
      ),
      id: 'per-funnel-allocated',
    },
  ];

  return (
    <>
      {hasMultipleOpenings && !isReadOnly && (
        <FormRadioGroup
          control={hookForm.control}
          name={`channels.${channelKey}.budgetAllocation`}
          options={budgetOptions}
          onChange={value =>
            handleBudgetAllocationChange(value as typeof budgetAllocation)
          }
        />
      )}

      {perOpeningBudget !== null && budgetAllocation === 'evenly' && (
        <Typography
          variant="body1"
          className={clsx(
            '!text-fx-neutral-600 -mt-3',
            hasMultipleOpenings && !isReadOnly ? 'ml-6' : 'ml-0',
          )}
        >
          {hasMultipleOpenings ? (
            <FormattedMessage
              defaultMessage="Spend per opening ({count} openings): {perOpeningBudget}"
              description="Budget line when total spend is split evenly across several openings"
              values={{
                count: Object.keys(openingBudgets).length,
                perOpeningBudget: (
                  <span className="font-medium">${perOpeningBudget}</span>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="Spend per opening (1 opening): {perOpeningBudget}"
              description="Budget line for a single opening when not split across multiple"
              values={{
                perOpeningBudget: (
                  <span className="font-medium">${perOpeningBudget}</span>
                ),
              }}
            />
          )}
        </Typography>
      )}

      {budgetAllocation === 'per-funnel' && (
        <Typography variant="body1" className="!text-fx-neutral-600 -mt-3 ml-6">
          <FormattedMessage
            defaultMessage="{count, plural, one {1 opening} other {{count} openings}}"
            values={{
              count: Object.keys(openingBudgets).length,
            }}
            description="ICU plural: how many jobs share this per-funnel budget split"
          />
        </Typography>
      )}
    </>
  );
}
